import { useSyncExternalStore } from "react";

/** "system" follows prefers-color-scheme; the others override it on this device. */
export type ThemeSetting = "system" | "light" | "dark";

const KEY = "quill.theme";
const COLORS = { light: "#f7f4ee", dark: "#262421" };
const media = window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();

function read(): ThemeSetting {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

let setting = read();

const effective = () => (setting === "system" ? (media.matches ? "dark" : "light") : setting);

/** Applies the theme to <html> and the browser chrome (status bar / title bar colour). */
export function applyTheme() {
  const root = document.documentElement;
  if (setting === "system") delete root.dataset.theme;
  else root.dataset.theme = setting;
  // One theme-color tag that tracks the effective theme (replaces the media-specific defaults).
  document.querySelectorAll('meta[name="theme-color"]').forEach((m, i) => {
    if (i === 0) {
      m.removeAttribute("media");
      m.setAttribute("content", COLORS[effective()]);
    } else m.remove();
  });
}

media.addEventListener("change", () => {
  applyTheme();
  listeners.forEach((l) => l());
});

export function setTheme(next: ThemeSetting) {
  setting = next;
  try {
    if (next === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    // ignore
  }
  applyTheme();
  listeners.forEach((l) => l());
}

export function useTheme(): ThemeSetting {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => setting,
  );
}
